import { computed, inject, provide, ref, watchEffect } from "vue";
import { useEventListener } from "@vueuse/core";
import { useTippy } from "vue-tippy";

import "./bulk-actions-panel.scss";

const PANEL_GROUP_KEY = Symbol("bulkActionsPanelGroup");

// The message can be long, so we increase the Flash duration.
export const DELETION_FLASH_DURATION = 9000;

// Provided by a bulk-actions toolbar parent. Each createOptionsPanel call in
// the parent's subtree (via useBulkActionsPanelGroup, called internally)
// registers with the group; opening any panel hides its siblings.
export function provideBulkActionsPanelGroup() {
  const panels = [];
  const group = {
    register(panel) {
      panels.push(panel);
    },
    hideOthers(self) {
      for (const p of panels) {
        if (p !== self) p.hide();
      }
    },
  };
  provide(PANEL_GROUP_KEY, group);
  return group;
}

export function useBulkActionsPanelGroup() {
  return inject(PANEL_GROUP_KEY, null);
}

// 1216px matches Bulma's $widescreen breakpoint used by the list components.
export function isLikelyUsingMobileOrTablet(widescreenWidth = 1216) {
  if (window.innerWidth < widescreenWidth) return true;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

// Auto-expand the bulk Actions strip on first selection (desktop only) and
// auto-contract when the user clears their selection. The didAutoExpand latch
// keeps a manual collapse from being stomped back open.
export function useAutoExpandActions(count, { initial = false } = {}) {
  const bulkActionsExpanded = ref(initial);
  let didAutoExpand = false;

  watchEffect(() => {
    if (isLikelyUsingMobileOrTablet()) return;
    const c = count.value;
    if (c >= 1 && !bulkActionsExpanded.value && !didAutoExpand) {
      bulkActionsExpanded.value = true;
      didAutoExpand = true;
    } else if (c === 0 && didAutoExpand) {
      bulkActionsExpanded.value = false;
      didAutoExpand = false;
    }
  });

  return bulkActionsExpanded;
}

// When everything selected is archived, we hide the archive button and the delete button shows.
function archiveSelectionState({ store, visibleRecords }) {
  const count = computed(() => store.value.selection.length);
  const states = computed(() => store.value.selectionArchiveState);
  const someArchived = computed(() => states.value.includes("archived"));
  const someUnarchived = computed(() => states.value.includes("unarchived"));
  const someUnknown = computed(() => states.value.includes("unknown"));
  const allSelectedArchived = computed(
    () => count.value > 0 && states.value.every((state) => state === "archived")
  );
  const allVisibleArchived = computed(
    () => visibleRecords.value.length > 0 && visibleRecords.value.every((record) => record.archived)
  );

  return {
    count,
    someArchived,
    someUnarchived,
    someUnknown,
    allSelectedArchived,
    allVisibleArchived,
  };
}

export function useBulkDeleteButton({ store, entityLabel, visibleRecords }) {
  const {
    count,
    someArchived,
    someUnarchived,
    someUnknown,
    allSelectedArchived,
    allVisibleArchived,
  } = archiveSelectionState({ store, visibleRecords });

  const someUnknownOrUnarchived = computed(() => someUnknown.value || someUnarchived.value);

  const deleteButtonVisible = computed(() => {
    if (count.value === 0) return store.value.archived === true || allVisibleArchived.value;
    return allSelectedArchived.value || (someArchived.value && someUnknownOrUnarchived.value);
  });

  const deleteButtonEnabled = computed(() => allSelectedArchived.value);

  const deleteHintText = computed(() => {
    if (count.value === 0) return `Select ${entityLabel}s first`;
    if (!deleteButtonEnabled.value) {
      return `All selected ${entityLabel}s must be archived before they can be deleted`;
    }
    return null;
  });

  const deleteButtonClasses = computed(() => {
    if (deleteButtonEnabled.value) return ["is-danger"];
    return ["is-outlined", "click-disabled", "half-opacity"];
  });

  return {
    deleteButtonVisible,
    deleteButtonEnabled,
    deleteHintText,
    deleteButtonClasses,
  };
}

export function useBulkArchiveButtons({ store, entityLabel }) {
  const { count, someArchived, someUnarchived, someUnknown, allSelectedArchived } =
    archiveSelectionState({ store, visibleRecords: computed(() => []) });

  const archiveButtonVisible = computed(() => !allSelectedArchived.value);

  const archiveButtonEnabled = computed(() => {
    if (count.value === 0) return store.value.archived === false;
    if (someUnknown.value) return true;
    if (someArchived.value && !someUnarchived.value) return false;
    return true;
  });

  const unarchiveButtonEnabled = computed(() => {
    if (count.value === 0) return store.value.archived === true;
    if (someUnknown.value) return true;
    return someArchived.value;
  });

  const archiveHintText = computed(() => {
    if (count.value === 0) return `Select ${entityLabel}s first`;
    if (archiveButtonEnabled.value) return null;
    if (someArchived.value && !someUnarchived.value) return "All selected are already archived";
    return null;
  });

  const unarchiveHintText = computed(() => {
    if (count.value === 0) return `Select ${entityLabel}s first`;
    if (unarchiveButtonEnabled.value) return null;
    if (someUnarchived.value && !someArchived.value) return "None of the selected are archived";
    return null;
  });

  const archiveButtonClasses = computed(() => {
    if (archiveButtonEnabled.value && count.value > 0) return ["is-warning"];
    if (archiveButtonEnabled.value) return ["is-outlined", "click-disabled"];
    return ["is-outlined", "click-disabled", "half-opacity"];
  });

  const unarchiveButtonClasses = computed(() => {
    if (unarchiveButtonEnabled.value && count.value > 0) return ["is-outlined", "is-success"];
    if (unarchiveButtonEnabled.value) return ["is-outlined", "click-disabled"];
    return ["is-outlined", "click-disabled", "half-opacity"];
  });

  return {
    count,
    archiveButtonEnabled,
    unarchiveButtonEnabled,
    archiveButtonVisible,
    archiveHintText,
    unarchiveHintText,
    archiveButtonClasses,
    unarchiveButtonClasses,
  };
}

// Listens for an event the bulk-actions toolbar dispatches when the user
// tries to act on an empty selection, and pulses for `duration` ms so the
// list can highlight its checkbox column.
export function useSelectionPrompt(eventName, { duration = 1200 } = {}) {
  const showingSelectionPrompt = ref(false);
  let timeout = null;

  useEventListener(document, eventName, () => {
    if (timeout) clearTimeout(timeout);
    showingSelectionPrompt.value = true;
    timeout = setTimeout(() => {
      showingSelectionPrompt.value = false;
      timeout = null;
    }, duration);
  });

  return showingSelectionPrompt;
}

// Tippy-anchored panel for a toolbar button. Returns { hide, show, toggle, isOpen }.
// Auto-coordinates with siblings via provideBulkActionsPanelGroup, when present.
//
// Args:
//   buttonRef:    () => HTMLElement|null. Anchor element.
//   openState:    Ref<boolean>. Mirrors panel visibility.
//   buildContent: (keyRef) => Tippy content. keyRef increments on every hide,
//                 so consumers can use it as :key to remount the panel content
//                 and reset its internal form state.
//   isEnabled:    () => boolean. When false, toggle is a no-op. Default: always enabled.
export function createOptionsPanel({
  buttonRef,
  openState,
  buildContent,
  isEnabled = () => true,
} = {}) {
  const keyRef = ref(0);
  const group = useBulkActionsPanelGroup();

  const tippyPanel = useTippy(buttonRef, {
    theme: "bulk-actions-panel",
    arrow: false,
    animation: "fade",
    trigger: "manual",
    interactive: true,
    appendTo: () => document.body,
    popperOptions: {
      placement: "bottom",
      strategy: "fixed",
      modifiers: [
        {
          name: "flip",
          options: {
            fallbackPlacements: ["bottom-start", "bottom-end", "left", "right", "top"],
            boundary: "viewport",
            padding: 100,
          },
        },
        {
          name: "preventOverflow",
          options: {
            boundary: "viewport",
            altAxis: true,
            padding: 100,
          },
        },
      ],
    },
    onHide() {
      openState.value = false;
      keyRef.value += 1;
    },
  });

  function hide() {
    tippyPanel.hide();
  }

  function show() {
    if (group) group.hideOthers(panel);
    openState.value = true;
    tippyPanel.setProps({ content: buildContent(keyRef) });
    tippyPanel.show();
  }

  function toggle() {
    if (!isEnabled()) return;
    tippyPanel.state.value.isShown ? hide() : show();
  }

  const panel = {
    hide,
    show,
    toggle,
    isOpen: () => openState.value,
  };
  if (group) group.register(panel);
  return panel;
}
